import * as React from "react";
import { useNavigate } from "react-router";
import { AdminProblem, useAdminApi, type AdminApi, type AdminSessionInfo } from "@/api/adminClient";
import { problemText } from "@/screens/format";

export type Load<T> = { state: "loading" } | { state: "ready"; data: T } | { state: "error"; message: string };

/**
 * Loads `fetch(api, session)` once the session is known, again whenever
 * `deps` change, and sends a signed-out admin to sign-in.
 */
export function useAdminLoad<T>(fetch: (api: AdminApi, session: AdminSessionInfo) => Promise<T>, deps: React.DependencyList, fallback: string): [Load<T>, () => void] {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [load, setLoad] = React.useState<Load<T>>({ state: "loading" });
  const [tick, setTick] = React.useState(0);
  const fetchRef = React.useRef(fetch);
  React.useEffect(() => {
    fetchRef.current = fetch;
  });

  React.useEffect(() => {
    let live = true;
    api
      .getSession()
      .then((s) => {
        if (!s) {
          void navigate("/signin", { replace: true });
          return null;
        }
        return fetchRef.current(api, s).then((data) => ({ data }));
      })
      .then((got) => {
        if (live && got) setLoad({ state: "ready", data: got.data });
      })
      .catch((err: unknown) => {
        if (!live) return;
        if (err instanceof AdminProblem && err.status === 401) return void navigate("/signin", { replace: true });
        setLoad({ state: "error", message: problemText(err, fallback) });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller names its inputs
  }, [api, navigate, tick, ...deps]);

  return [load, () => setTick((n) => n + 1)];
}

/** Runs one admin write: busy state, the server's problem text, sign-in on 401. */
export function useAdminAction() {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const run = <T,>(call: Promise<T>, done: (value: T) => void) => {
    setBusy(true);
    setProblem(null);
    call
      .then((value) => {
        setBusy(false);
        done(value);
      })
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof AdminProblem && err.status === 401) return void navigate("/signin", { replace: true });
        setProblem(problemText(err, "Could not reach the server."));
      });
  };
  return { busy, problem, setProblem, run };
}

